/**
 * Composes hooks for search term, pagination reset, filter payload, and search side effects
 * for the external PA orders worklist table.
 */
import { useExternalFilterAndSearchEffect } from "./useExternalFilterAndSearchEffect";
import { useExternalFilterPayload } from "./useExternalFilterPayload";
import { useExternalPaginationReset } from "./useExternalPaginationReset";
import { useExternalSearchTerm } from "./useExternalSearchTerm";
import { ExternalTableState } from "./useExternalTableData";
import { useExternalValidFilters } from "./useExternalValidFilters";

/**
 * Orchestrates filter and search state for the external PA orders table.
 * Composes useExternalSearchTerm, useExternalPaginationReset, useExternalValidFilters,
 * and useExternalFilterPayload to produce a unified filter/search API and triggers
 * data re-fetching via useExternalFilterAndSearchEffect.
 *
 * @param initialDataFetchingExecuted - Whether the initial data load has completed
 * @param setTableData - State setter for table row data
 * @param setExternalTableState - State setter for the orders loading/data lifecycle
 * @returns Filter payload, search term controls, pagination reset counter, and active filters
 */
export const useExternalFilterAndSearch = (
  initialDataFetchingExecuted: boolean,
  setTableData: React.Dispatch<React.SetStateAction<any[]>>,
  setExternalTableState: React.Dispatch<
    React.SetStateAction<ExternalTableState>
  >,
) => {
  const { searchTerm, setSearchTerm } = useExternalSearchTerm();
  const { paginationResetCount, setPaginationResetCount } =
    useExternalPaginationReset();
  const validFilters = useExternalValidFilters();
  const { filterPayload } = useExternalFilterPayload(
    validFilters,
    searchTerm,
    initialDataFetchingExecuted,
  );

  useExternalFilterAndSearchEffect(
    filterPayload,
    initialDataFetchingExecuted,
    setTableData,
    setExternalTableState,
    setPaginationResetCount,
  );

  return {
    filterPayload,
    searchTerm,
    setSearchTerm,
    paginationResetCount,
    setPaginationResetCount,
    validFilters,
  };
};
